// L'ETAPA 7 DEL SSM: L'ACCIÓ.
//
// Fora de React, amb un sol import (`canvisSSM.js`, que tampoc no en té de fora). Es prova sola:
// `npm run prova:accions`.
//
// ── QUÈ ÉS, I PER QUÈ ÉS LA MÉS CURTA I LA MÉS FÀCIL DE FER MALAMENT ─────────────────────────
//
// L'etapa 7 **rep una llista tancada**: els canvis que a l'etapa 6 han passat els dos eixos. No
// s'hi decideix res de nou; s'hi converteix cada canvi acordat en **qui fa què, quan, i com se
// sabrà que ha passat**.
//
// I té dues maneres d'anar malament que es poden detectar:
//
//   · **Un canvi acordat sense cap acció.** És la fallada clàssica: s'ha discutit, s'ha acordat,
//     i no s'ha fet res. Un acord sense acció és una reunió.
//   · **Una acció sense qui.** «S'hauria de fer» no és una acció. Si no hi ha nom, no passarà.
//
// ── I EL BUCLE, QUE ÉS EL QUE LA MAJORIA D'EINES ES DEIXEN ───────────────────────────────────
//
// **Actuar canvia la situació.** El SSM no s'acaba a l'etapa 7: quan les accions passen, la
// situació ja no és la mateixa que es va explorar a l'etapa 1, i el que hi ha és **una situació
// nova per tornar a mirar**. Checkland ho dibuixa com un cicle i no com una fila de set caselles,
// i és el motiu pel qual la barra lateral d'aquesta aplicació **no és una barra de progrés**.
//
// Per això aquesta etapa acaba dient-ho, i no amb un «ja està».

import { acordats, candidats } from './canvisSSM';

export const ESTATS = ['todo', 'doing', 'done'];

const net = (v) => String(v || '').trim();

// Les accions, agrupades pel canvi acordat de què pengen.
export function perCanvi(data) {
  const acords = acordats(candidats(data));
  const accions = data?.implementation?.actions || [];
  return acords.map((c) => ({
    canvi: c,
    accions: accions.filter((a) => a.changeId === c.id),
  }));
}

// Accions que han quedat penjades: el canvi de què penjaven ja no està acordat.
//
// Passa quan es torna a l'etapa 6 i es canvia un judici, i **és bo que passi** —repensar-hi és la
// feina—, però l'acció era feina també. Es diu, com els judicis orfes de l'etapa 6 i les
// respostes òrfenes de la 5.
export function accionsOrfes(data) {
  const vius = new Set(acordats(candidats(data)).map((c) => c.id));
  return (data?.implementation?.actions || []).filter((a) => !vius.has(a.changeId));
}

export const netejaAccions = (data) => {
  const orfes = new Set(accionsOrfes(data).map((a) => a.id));
  return (data?.implementation?.actions || []).filter((a) => !orfes.has(a.id));
};

export const totesLesAccions = (data) =>
  (data?.implementation?.actions || []).filter((a) => !accionsOrfes(data).some((o) => o.id === a.id));

export function recompte(data) {
  const totes = totesLesAccions(data);
  return {
    total: totes.length,
    fetes: totes.filter((a) => a.status === 'done').length,
    encurs: totes.filter((a) => a.status === 'doing').length,
  };
}

// Els avisos. Es diuen i no impedeixen res, com a la resta d'etapes.
export function avisos(data) {
  const fora = [];
  const grups = perCanvi(data);
  // LA FALLADA CLÀSSICA: acordat i sense fer res. Un acord sense acció és una reunió.
  const muts = grups.filter((g) => g.accions.length === 0);
  if (muts.length) fora.push({ clau: 'ssm.acSenseAccio', n: muts.length });
  // «S'hauria de fer» no és una acció.
  const senseQui = totesLesAccions(data).filter((a) => net(a.text) && !net(a.who));
  if (senseQui.length) fora.push({ clau: 'ssm.acSenseQui', n: senseQui.length });
  const senseQuan = totesLesAccions(data).filter((a) => net(a.text) && net(a.who) && !net(a.when));
  if (senseQuan.length) fora.push({ clau: 'ssm.acSenseQuan', n: senseQuan.length });
  // Sense «com ho sabrem», no es pot tancar el bucle: l'etapa 7 desemboca en una situació nova
  // que caldrà tornar a mirar, i sense mesura no se sabrà què ha canviat.
  const senseCom = totesLesAccions(data).filter((a) => net(a.text) && !net(a.how));
  if (senseCom.length) fora.push({ clau: 'ssm.acSenseCom', n: senseCom.length });
  const orfes = accionsOrfes(data);
  if (orfes.length) fora.push({ clau: 'ssm.acOrfes', n: orfes.length });
  return fora;
}

// Si val la pena dir que el cicle es pot tornar a començar. **No és «ja està»**: és que la
// situació ja no és la mateixa i toca tornar-la a mirar.
export const potTancarElCicle = (data) => {
  const r = recompte(data);
  return r.total > 0 && r.fetes > 0;
};

const ACCIONS = { ESTATS, perCanvi, accionsOrfes, netejaAccions, totesLesAccions,
                  recompte, avisos, potTancarElCicle };
export default ACCIONS;
